import { useState } from "react"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@components/ui/button"
import { useAddReferralMutation } from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import type { ISearchItem } from "@commonTypes/searchResponse"
import { AccountSearchSelect } from "./AccountSearchSelect"

type TAddReferralFormProps = {
  referrer_guid: string
  account_id: string
}

export function AddReferralForm({
  referrer_guid,
  account_id,
}: TAddReferralFormProps) {
  const [selected, setSelected] = useState<ISearchItem | null>(null)
  const queryClient = useQueryClient()
  const { mutate, isPending } = useAddReferralMutation()

  const handleSubmit = () => {
    if (!selected?.account_id) return
    mutate(
      { referrer_guid, account_id: selected.account_id },
      {
        onSuccess: () => {
          toast.success("Referral added successfully!")
          setSelected(null)
        },
        onError: (error) => toast.error(`Error adding referral: ${error}`),
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountReferrals(account_id),
          })
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountRewards(account_id),
          })
        },
      },
    )
  }

  return (
    <div className="tw:space-y-4 tw:rounded-lg tw:border tw:border-gray-200 tw:p-4">
      <div className="tw:pb-3 tw:border-b tw:border-gray-200">
        <h3 className="tw:mb-0 tw:font-semibold">Add Referral</h3>
      </div>
      <AccountSearchSelect
        selected={selected}
        onSelect={setSelected}
        onClear={() => setSelected(null)}
        excludeAccountId={account_id}
        label="Referred account"
      />
      <div className="tw:flex tw:gap-2">
        <Button
          onClick={handleSubmit}
          disabled={!selected?.account_id || isPending}
        >
          {isPending && (
            <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
          )}
          Add Referral
        </Button>
        <Button
          variant="outline"
          onClick={() => setSelected(null)}
          disabled={!selected || isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
